import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { AddressFields } from '@/components/AddressFields'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { FieldGroup } from '@/components/ui/field'
import { parseError } from '@/lib/errors'
import { type OrgBusiness, updateBusiness } from '@/models/business'

interface FormData {
  line1: string
  line2: string
  city: string
  state: string
  zip: string
}

interface EditBusinessAddressDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  business: OrgBusiness
  orgId: string
}

export function EditBusinessAddressDialog({
  open,
  onOpenChange,
  onSuccess,
  business,
  orgId,
}: EditBusinessAddressDialogProps) {
  const [loading, setLoading] = useState(false)

  const { control, handleSubmit, reset, setValue } = useForm<FormData>({
    defaultValues: { line1: '', line2: '', city: '', state: '', zip: '' },
  })

  useEffect(() => {
    if (open) {
      reset({
        line1: business.address?.line1 || '',
        line2: business.address?.line2 || '',
        city: business.address?.city || '',
        state: business.address?.state || '',
        zip: business.address?.zip || '',
      })
    }
  }, [open, business, reset])

  async function onSubmit(data: FormData) {
    setLoading(true)
    try {
      await updateBusiness(orgId, {
        address: {
          line1: data.line1.trim(),
          line2: data.line2.trim(),
          city: data.city.trim(),
          state: data.state,
          zip: data.zip.trim(),
        },
      })
      onSuccess()
      toast.success('Address updated')
      onOpenChange(false)
    } catch (err) {
      toast.error(parseError(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) reset()
        onOpenChange(value)
      }}
    >
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>Edit Address</DialogTitle>
          </DialogHeader>
          <FieldGroup className="mt-4">
            <AddressFields
              control={control}
              setValue={setValue}
              names={{ line1: 'line1', line2: 'line2', city: 'city', state: 'state', zip: 'zip' }}
              required
            />
          </FieldGroup>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={loading ? 'Saving...' : undefined}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
